import { countsAsDayVisit, dayVisitCounts, type DayVisit } from './day-visits'
import { money } from './ui'

// The worker's day tally, factored out of screens/today.ts so the arithmetic
// is testable without a browser (blocked-outcome.test.mjs).
//
// "Done 4 of 9 · £180.00 taken" — plus " · 1 couldn't" once a visit carries a
// "Couldn't do it" outcome (blocked): still booked, but it did not happen and
// the owner needs to know it sits in their review pile.

export interface TallyVisit extends DayVisit {
  state: string
  payment_status?: string | null
  amount_pence?: number | null
  blocked_reason?: string | null
}

// `hidePrices`: a worker who is not shown prices (0021) gets no money figure.
// `work`: the money worked so far of the day's, after the count (Liam, 22 Oct 2026, on "Done 5 of 7 · £0.00 taken":
// "it could be even more satisfying perhaps also have amount of money and amount of money done"): "Done 5 of 7 · £225.00
// of £340.00 · £0.00 taken". A job marked Couldn't do it is not in the day's money.
export function todayTallyLine(visits: TallyVisit[], offlineCopy = false, hidePrices = false, work = false): string {
  visits = visits.filter(countsAsDayVisit)
  const count = dayVisitCounts(visits)
  const done = visits.filter((c) => c.state === 'done')
  const taken = done.filter((c) => c.payment_status === 'paid').reduce((sum, c) => sum + (c.amount_pence ?? 0), 0)
  const couldnt = visits.filter((c) => c.state === 'booked' && c.blocked_reason).length
  const suffix = couldnt > 0 ? ` · ${couldnt} couldn't` : ''
  const money_ = hidePrices ? '' : ` · ${money(taken)} taken`
  const worked = work && !hidePrices ? workWords(visits) : ''
  return `Done ${count.done} of ${count.total}${worked}${money_}${suffix}${offlineCopy ? ' (offline copy)' : ''}`
}

// " · £225.00 of £340.00": the done jobs' prices of every job's still to happen or done; '' with no prices at all.
export function workWords(visits: TallyVisit[]): string {
  const counted = visits.filter(countsAsDayVisit).filter((c) => !(c.state === 'booked' && c.blocked_reason))
  const all = counted.reduce((sum, c) => sum + (c.amount_pence ?? 0), 0)
  if (all <= 0) return ''
  const done = counted.filter((c) => c.state === 'done').reduce((sum, c) => sum + (c.amount_pence ?? 0), 0)
  return ` · ${money(done)} of ${money(all)}`
}
